<template>
  <button
    class="camera-tts-btn flex items-center gap-2 px-3 py-2 rounded"
    :disabled="loading"
    :style="btnStyle"
    @click="handleClick"
  >
    <feather-icon :icon="iconName" class="w-4 h-4" />
    <span>{{ label }}</span>
  </button>
</template>

<script>
export default {
  name: 'CameraTtsButton',
  props: {
    text: { type: String, required: true },
    lang: { type: String, default: null },
    label: { type: String, default: 'TTS' }
  },
  data() {
    return {
      loading: false,
      playing: false,
      blobUrl: null,
      audioEl: null,
    }
  },
  computed: {
    iconName() {
      if (this.loading) return 'LoaderIcon';
      if (this.playing) return 'StopCircleIcon';
      return 'Volume2Icon';
    },
    btnStyle() {
      if (this.loading) return 'border:none;cursor:not-allowed;background:#F9FAFB;color:#9CA3AF;font-size:14px;font-weight:500;';
      if (this.playing) return 'border:none;cursor:pointer;background:#FEF2F2;color:#EF4444;font-size:14px;font-weight:600;';
      return 'border:none;cursor:pointer;background:#F5F3FF;color:#7E72F2;font-size:14px;font-weight:600;';
    }
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    handleClick() {
      if (this.loading) return;
      if (this.playing) {
        this.stop();
        return;
      }
      if (this.lang) {
        this.playApiTts();
      } else {
        this.playBrowserTts();
      }
    },

    async playApiTts() {
      if (!this.text) return;
      if (this.blobUrl) {
        this.playFromBlobUrl();
        return;
      }
      this.loading = true;
      try {
        const ttsResponse = await this.$store.dispatch('miras/TEXT_TO_SPEECH', {
          text: this.text,
          lang: this.lang
        });
        if (ttsResponse && ttsResponse.audioUrl) {
          const response = await this.$http.get(ttsResponse.audioUrl, { responseType: 'blob' });
          if (response && response.data) {
            const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
            this.blobUrl = URL.createObjectURL(audioBlob);
            this.$nextTick(() => {
              this.playFromBlobUrl();
            });
          }
        }
      } catch (err) {
        console.error('CameraTtsButton playApiTts error:', err);
      } finally {
        this.loading = false;
      }
    },

    playFromBlobUrl() {
      if (!this.audioEl) {
        this.audioEl = new Audio(this.blobUrl);
        this.audioEl.onended = () => { this.playing = false; };
        this.audioEl.onerror = () => { this.playing = false; };
      }
      this.audioEl.play();
      this.playing = true;
    },

    playBrowserTts() {
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const msg = new SpeechSynthesisUtterance();
      msg.text = this.text;
      msg.onend = () => { this.playing = false; };
      msg.onerror = () => { this.playing = false; };
      this.playing = true;
      window.speechSynthesis.speak(msg);
    },

    stop() {
      if (this.audioEl) {
        this.audioEl.pause();
        this.audioEl.currentTime = 0;
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      this.playing = false;
    },

    cleanup() {
      this.stop();
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
      this.audioEl = null;
    }
  }
}
</script>
